import { useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { alpha, Card, Checkbox, Icon, IconBtn, Input, MoreMenu, Popover, Skeleton, T, type MoreMenuItemSpec } from "@v0y4ge/jrni-ui";
import { isAdmin } from "./access";
import { AuditTrailDrawer } from "./AuditTrail";
import type { AuditObjectType } from "../lib/audit";
import { useT } from "../lib/i18n";

/**
 * Shared list-screen composites — verbatim ports of the prototype's StatTile,
 * FilterBar, EmptyState, ExplainerFooter, FilterDropdown. Render our look,
 * built on jrni-ui primitives + tokens.
 */

export function StatTile({ label, value, delta, deltaPos, icon, accent = T.p1, onClick, testid }: {
  label: ReactNode; value: ReactNode; delta?: ReactNode; deltaPos?: boolean; icon?: ReactNode; accent?: string; onClick?: () => void; testid?: string;
}) {
  const t = useT();
  const interactive = !!onClick;
  return (
    <Card testid={testid} onClick={onClick}
      style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8, cursor: interactive ? "pointer" : "default", transition: "transform 120ms cubic-bezier(0.4,0,0.2,1), box-shadow 120ms" }}
      onMouseEnter={interactive ? (e) => { e.currentTarget.style.borderColor = alpha(accent, "60"); e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = T.shadowChrome; } : undefined}
      onMouseLeave={interactive ? (e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } : undefined}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: T.soft, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.04 }}>{label}</span>
        {icon ? <span style={{ width: 28, height: 28, borderRadius: 6, background: alpha(accent, "20"), color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icon}</span> : null}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: T.n1, letterSpacing: -0.5 }}>{value}</span>
        {delta ? <span style={{ fontSize: 12, fontWeight: 600, color: deltaPos ? T.green1 : T.red1 }}>{deltaPos ? "↑" : "↓"} {delta}</span> : null}
      </div>
      {interactive ? <div style={{ fontSize: 11, color: accent, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, marginTop: -2 }}>{t("shell.composites.viewDetails", "View details")}<Icon.arrow size={10} /></div> : null}
    </Card>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return <div style={{ padding: "12px 24px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{children}</div>;
}

/**
 * EmptyState — THE no-data / no-match / couldn't-load presentation for the whole
 * app. Roughly 25 screens render it, so its rhythm is defined here once and
 * nowhere else.
 *
 * Two things it deliberately does NOT do:
 *  · It does not use flex `gap` for vertical rhythm. `gap` compounds with the
 *    default `h3`/`p` margins, which is what made the old version read as a
 *    ~70px hole between title and body. Every gap here is an explicit margin
 *    on a margin-reset element.
 *  · It is NOT a card by default. Many callers already render it inside a white
 *    panel (the workforce boards, `<Card>` bodies), and a card there would nest
 *    two borders. Page-level list screens opt in with `variant="card"`.
 */
export type EmptyStateTone = "neutral" | "danger" | "warn" | "success";

export function EmptyState({ icon, title, body, action, secondaryAction, tone = "neutral", variant = "plain", compact = false, style, testid }: {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  /** Sits beside `action` — e.g. "Clear filters" next to "New ticket type". */
  secondaryAction?: ReactNode;
  tone?: EmptyStateTone;
  /** "card" = white surface + border, for a page-level empty. "plain" inside an existing panel. */
  variant?: "card" | "plain";
  /** Tighter scale for small panels and drawer bodies. */
  compact?: boolean;
  style?: CSSProperties;
  testid?: string;
}) {
  const accent = tone === "danger" ? T.red1 : tone === "warn" ? T.orange1 : tone === "success" ? T.green1 : T.p1;
  const dot = compact ? 40 : 52;
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center",
        padding: compact ? "28px 20px" : "48px 24px",
        ...(variant === "card" ? { background: T.white, border: `1px solid ${T.border}`, borderRadius: 10 } : {}),
        ...style,
      }}
    >
      {icon ? (
        <span style={{ width: dot, height: dot, borderRadius: 999, background: alpha(accent, 12), color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: compact ? 12 : 16, flexShrink: 0 }}>{icon}</span>
      ) : null}
      <h3 style={{ margin: 0, fontSize: compact ? 14.5 : 16.5, fontWeight: 600, color: T.n1, letterSpacing: -0.1 }}>{title}</h3>
      {body ? (
        <p style={{ margin: "6px 0 0", fontSize: 13.5, color: T.soft, lineHeight: 1.55, maxWidth: 400, textWrap: "pretty" }}>{body}</p>
      ) : null}
      {action || secondaryAction ? (
        <div style={{ marginTop: compact ? 14 : 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}

/**
 * LoadState — the one place the app decides between "loading", "this failed" and
 * "there is nothing here".
 *
 * WHY THIS EXISTS (2026-07-31, operator: "fix it so I never have this issue again,
 * it's frightening"): the messaging library twice rendered a calm, confident empty
 * table while the underlying read had failed. An empty state over a failed read
 * does not read as "try again" — it reads as *your work is gone*. Both faults hid
 * for hours because the screen looked normal.
 *
 * THE RULE: a failed read and an empty result must never render the same way.
 * Stores expose `error` alongside their data; screens hand both to this component
 * and stop making the choice themselves. Adopt it by replacing the empty-state
 * branch of a list screen:
 *
 *     <LoadState loading={store.loading} error={store.error} empty={rows.length === 0}
 *       onRetry={() => store.reload()}
 *       emptyState={<EmptyState variant="card" title=… body=… action=… />}>
 *       {theTable}
 *     </LoadState>
 *
 * `noun` names the thing in the error copy ("Couldn't load your messages").
 */
export function LoadState({ loading, error, empty, noun, onRetry, emptyState, loadingLabel, skeleton, children, testid }: {
  loading?: boolean;
  /** Set ONLY when the read genuinely failed — never for an empty result. */
  error?: string | null;
  empty?: boolean;
  /** Plural noun for the error headline, e.g. "messages". */
  noun?: string;
  onRetry?: () => void;
  emptyState?: ReactNode;
  loadingLabel?: ReactNode;
  /**
   * Loading TREATMENT. ADDITIVE and OPTIONAL — omitted, the loading branch is
   * the centred "Loading…" line it has always been, so every existing call
   * site is untouched.
   *
   * `"table"` and `"cards"` are the two shared presets, built from jrni-ui's
   * `Skeleton` primitive at the real row rhythm / the real `CARD_GRID` minimum,
   * so a list holds its shape instead of collapsing to a ~40px strip and
   * snapping back when the read lands. A `ReactNode` is accepted for the rare
   * screen whose loading shape is neither.
   *
   * They are PRESETS on purpose. `loadingLabel` already accepts a node, so any
   * screen could hand-roll its own skeleton today — which is exactly how the
   * app would end up with 25 different loading treatments. Pick a preset.
   */
  skeleton?: "table" | "cards" | ReactNode;
  children?: ReactNode;
  testid?: string;
}) {
  const t = useT();
  if (error) {
    return (
      <div role="alert" data-testid={testid ? `${testid}-error` : "loadstate-error"}
        style={{ padding: "22px 24px", background: T.red2, border: "1px solid #F5C2D1", borderLeft: `3px solid ${T.red1}`, borderRadius: 11, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <Icon.warn size={18} style={{ color: T.red1, flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>
            {noun ? t("shell.loadState.failedTitleNoun", "Couldn't load your {noun}").replace("{noun}", noun) : t("shell.loadState.failedTitle", "Couldn't load this")}
          </div>
          {/* The reassurance is the point: an empty screen after a failed read reads
              as data loss, and that is what frightened the operator. */}
          <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.55, marginTop: 5 }}>
            {t("shell.loadState.failedBody", "Nothing has been deleted — this could not be read, so nothing is shown. Retry, and if it keeps failing the service is unavailable.")}
          </div>
          <div style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono, marginTop: 7, wordBreak: "break-word" }}>{error}</div>
          {onRetry ? (
            <button type="button" onClick={onRetry} data-testid={testid ? `${testid}-retry` : "loadstate-retry"}
              style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34, padding: "0 14px", borderRadius: 8, border: `1px solid ${T.red1}`, background: T.white, color: T.red1, fontFamily: T.font, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              <Icon.refresh size={14} />{t("common.actions.retry", "Retry")}
            </button>
          ) : null}
        </div>
      </div>
    );
  }
  if (loading) {
    if (skeleton !== undefined && skeleton !== null && skeleton !== false) {
      const label = typeof loadingLabel === "string" ? loadingLabel : t("shell.loadState.loading", "Loading…");
      return (
        // The skeleton itself is `aria-hidden` (jrni-ui `Skeleton`), so the
        // status role + label is what a screen reader gets — the same sentence
        // the text treatment shows, not silence.
        <div role="status" aria-busy="true" aria-label={label}
          data-testid={testid ? `${testid}-loading` : "loadstate-loading"}>
          {skeleton === "table" ? <TableSkeleton /> : skeleton === "cards" ? <CardsSkeleton /> : skeleton}
        </div>
      );
    }
    return (
      <div data-testid={testid ? `${testid}-loading` : "loadstate-loading"} style={{ padding: "40px 24px", textAlign: "center", color: T.soft, fontSize: 14 }}>
        {loadingLabel || t("shell.loadState.loading", "Loading…")}
      </div>
    );
  }
  if (empty) return <>{emptyState ?? null}</>;
  return <>{children}</>;
}

/**
 * The two `LoadState skeleton` presets. Both are token-only and both mirror the
 * geometry of the thing they stand in for, which is the whole point — a
 * skeleton that doesn't match the loaded layout produces the same jump the bare
 * "Loading…" line does.
 */
const SKELETON_ROWS = 6;

function TableSkeleton() {
  // Matches jrni-ui `Table`: neutral-5 hairline + md radius wrapper, a
  // neutral-6 header strip at the header cell's 10px/16px padding, then rows at
  // the body cell's 12px/16px.
  return (
    <div style={{ background: T.white, border: `1px solid ${T.n5}`, borderRadius: T.radius.md, overflow: "hidden" }}>
      {/* 10px block padding is jrni-ui `Table`'s own header-cell literal — copied
          deliberately so the skeleton header strip is the same height as the
          header it stands in for. Everything else is on the token scales. */}
      <div style={{ paddingBlock: 10, paddingInline: T.space[4], background: T.n6, borderBottom: `1px solid ${T.n5}`, display: "flex", gap: T.space[4] }}>
        <Skeleton width="22%" height={13} variant="text" />
        <Skeleton width="16%" height={13} variant="text" />
        <Skeleton width="12%" height={13} variant="text" />
      </div>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <div key={i} style={{ paddingBlock: T.space[3], paddingInline: T.space[4], borderBottom: i === SKELETON_ROWS - 1 ? "none" : `1px solid ${T.n5}`, display: "flex", alignItems: "center", gap: T.space[4] }}>
          <Skeleton width={26} height={26} variant="circle" />
          <Skeleton width="30%" height={14} variant="text" />
          <Skeleton width="18%" height={14} variant="text" />
          <span style={{ flex: 1 }} />
          <Skeleton width={64} height={20} variant="rect" />
        </div>
      ))}
    </div>
  );
}

function CardsSkeleton() {
  // 300px minimum = `CARD_GRID` in shell/list-composites. Inlined rather than
  // imported because list-composites imports FROM this module — importing back
  // would make the pair circular.
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: T.space[4] }}>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <div key={i} style={{ background: T.white, border: `1px solid ${T.n5}`, borderRadius: T.radius.md, padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[2] }}>
          <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
            <Skeleton width={32} height={32} variant="rect" />
            <Skeleton width="55%" height={15} variant="text" />
          </div>
          <Skeleton width="100%" height={13} variant="text" />
          <Skeleton width="72%" height={13} variant="text" />
          <div style={{ display: "flex", gap: T.space[2], marginTop: T.space[1] }}>
            <Skeleton width={70} height={22} variant="rect" />
            <Skeleton width={54} height={22} variant="rect" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ExplainerFooter({ children }: { children: ReactNode }) {
  return (
    // Owns its own vertical air on BOTH sides. It reads as an intro banner above
    // the list on the Events rail and as a footer below it elsewhere, and before
    // this the "below" side was left to each screen — which is why the banner sat
    // flush against the empty-state card on Events and Templates (0px), 4px on
    // Ticket Groups and 16px on Archive. Screens that follow it now zero their own
    // top padding so the 16px here is the single source of that gap.
    <div style={{ marginTop: 16, marginBottom: 16, padding: 16, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, display: "flex", gap: 12, alignItems: "center" }}>
      <span style={{ width: 28, height: 28, borderRadius: 6, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.info size={14} /></span>
      <div style={{ flex: 1, fontSize: 13, color: T.n1, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

const popoverItemStyle: CSSProperties = { display: "flex", width: "100%", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left", alignItems: "center" };

export function FilterDropdown({ label, options, active, onPick, onClear }: { label: ReactNode; options: string[]; active?: boolean; onPick: (o: string) => void; onClear: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)} style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 10px", border: `1px solid ${active ? T.p1 : T.n4}`, borderRadius: 4, background: active ? T.p4 : T.white, color: active ? T.p1 : T.n1, cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: T.font }}>
        {label}
        {active ? <span onClick={(e) => { e.stopPropagation(); onClear(); }} style={{ display: "inline-flex" }}><Icon.x size={10} /></span> : <Icon.chev size={12} />}
      </button>
      {open ? (
        <Popover open onClose={() => setOpen(false)} align="left" offset={4} width={220} panelStyle={{ left: 0, right: "auto" }}>
          <ul style={{ listStyle: "none", margin: 0, padding: 4 }}>
            <li><button onClick={() => { onClear(); setOpen(false); }} style={popoverItemStyle}><span style={{ fontSize: 13, color: T.soft }}>{t("shell.composites.any", "Any")}</span></button></li>
            {options.map((o) => <li key={o}><button onClick={() => { onPick(o); setOpen(false); }} style={popoverItemStyle}><span style={{ fontSize: 13, color: T.n1 }}>{o}</span></button></li>)}
          </ul>
        </Popover>
      ) : null}
    </div>
  );
}

/* ── FilterToolbar — the app's advanced list filter ──────────────────────
 *
 * ONE filter surface for every list screen that needs more than a search box:
 * free text + any number of MULTI-SELECT facets with live counts + a row of
 * removable active-filter chips. `FilterDropdown` above stays for the simple
 * single-value case (one dropdown, "Any" to clear); reach for this when a
 * screen has several dimensions that combine — status AND staff AND service AND
 * source — and the user needs to see what is currently narrowing the list.
 *
 * The toolbar is presentational: the screen owns `query` and `selected`
 * ({facetId: value[]}) and does its own matching, so a facet can filter on
 * anything (a joined record, an answered booking question) without this
 * component knowing the data shape. Counts are supplied per option — a facet
 * shows how many rows each value WOULD match, which is what makes it feel
 * powerful rather than a row of empty dropdowns.
 *
 * First consumer: the Appointments day book (2026-08-06).
 */
export interface FilterFacetOption { value: string; label: string; count?: number }
export interface FilterFacet {
  id: string;
  label: string;
  icon?: ReactNode;
  options: FilterFacetOption[];
  /** Force the in-menu search field on/off. Default: on above 8 options. */
  searchable?: boolean;
}

const FACET_SEARCH_THRESHOLD = 8;
/** Every control in the toolbar stands on the same baseline height as the
 *  design-system Dropdown (40px) — search field, facet triggers and the scope
 *  select line up exactly. */
const FILTER_CONTROL_H = 40;

function FacetMenu({ facet, values, onToggle, onClear, testid }: {
  facet: FilterFacet; values: string[]; onToggle: (v: string) => void; onClear: () => void; testid?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const active = values.length > 0;
  const searchable = facet.searchable ?? facet.options.length > FACET_SEARCH_THRESHOLD;
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? facet.options.filter((o) => o.label.toLowerCase().includes(needle)) : facet.options;
  }, [facet.options, q]);
  return (
    <div style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={testid} aria-expanded={open}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, height: FILTER_CONTROL_H, padding: "0 12px",
          border: `1px solid ${active ? T.p1 : T.n4}`, borderRadius: 4, background: active ? T.p4 : T.white,
          color: active ? T.p1 : T.n1, cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: T.font }}>
        {facet.icon ? <span style={{ display: "inline-flex", color: active ? T.p1 : T.soft }}>{facet.icon}</span> : null}
        {facet.label}
        {active ? (
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, background: T.p1, color: T.white, fontSize: 11, fontWeight: 700 }}>{values.length}</span>
        ) : <Icon.chev size={12} />}
      </button>
      {open ? (
        <Popover open onClose={() => setOpen(false)} align="left" offset={4} width={268} panelStyle={{ left: 0, right: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", maxHeight: 320 }}>
            {searchable ? (
              <div style={{ padding: 8, borderBottom: `1px solid ${T.border}` }}>
                <Input size="sm" autoFocus value={q} onChange={(e) => setQ(e.target.value)} leading={<Icon.search size={13} />}
                  placeholder={t("shell.composites.filterSearch", "Search") + " " + facet.label.toLowerCase()} />
              </div>
            ) : null}
            <ul style={{ listStyle: "none", margin: 0, padding: 4, overflowY: "auto", flex: 1 }}>
              {shown.length === 0 ? (
                <li style={{ padding: "10px 12px", fontSize: 12.5, color: T.soft }}>{t("shell.composites.noMatches", "No matches")}</li>
              ) : shown.map((o) => {
                const on = values.includes(o.value);
                return (
                  <li key={o.value}>
                    <button type="button" onClick={() => onToggle(o.value)}
                      style={{ ...popoverItemStyle, gap: 9, minHeight: 34, background: on ? T.p4 : "transparent" }}
                      onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = T.n6; }}
                      onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}>
                      <Checkbox checked={on} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                      {typeof o.count === "number" ? <span style={{ fontSize: 11.5, color: T.soft, fontVariantNumeric: "tabular-nums" }}>{o.count}</span> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            {active ? (
              <div style={{ borderTop: `1px solid ${T.border}`, padding: 6 }}>
                <button type="button" onClick={() => { onClear(); setOpen(false); }}
                  style={{ ...popoverItemStyle, justifyContent: "center", fontSize: 12.5, color: T.p1, fontWeight: 600 }}>
                  {t("shell.composites.clearFacet", "Clear")} {facet.label.toLowerCase()}
                </button>
              </div>
            ) : null}
          </div>
        </Popover>
      ) : null}
    </div>
  );
}

export function FilterToolbar({ query, onQueryChange, searchPlaceholder, facets, selected, onToggle, onClearFacet, onClearAll, leading, trailing, summary, testid }: {
  query: string;
  onQueryChange: (v: string) => void;
  searchPlaceholder?: string;
  facets: FilterFacet[];
  /** {facetId: selected values}. */
  selected: Record<string, string[]>;
  onToggle: (facetId: string, value: string) => void;
  onClearFacet: (facetId: string) => void;
  onClearAll: () => void;
  /** Rendered before the search field — e.g. the location scope select. */
  leading?: ReactNode;
  /** Rendered at the far right — counts, view toggle, export. */
  trailing?: ReactNode;
  /** Result summary shown beside the chips, e.g. "12 of 40". */
  summary?: ReactNode;
  testid?: string;
}) {
  const t = useT();
  const chips = facets.flatMap((f) => (selected[f.id] ?? []).map((v) => ({
    facet: f, value: v, label: f.options.find((o) => o.value === v)?.label ?? v,
  })));
  const anyFilter = chips.length > 0 || query.trim().length > 0;
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column" }}>
      <FilterBar>
        {leading}
        <Input full={false} value={query} onChange={(e) => onQueryChange(e.target.value)}
          leading={<Icon.search size={14} />} testid={testid ? `${testid}-search` : undefined}
          placeholder={searchPlaceholder || t("shell.composites.searchPlaceholder", "Search…")}
          style={{ minWidth: 300 }} />
        <span style={{ width: 1, height: 22, background: T.border, margin: "0 2px" }} />
        {facets.map((f) => (
          <FacetMenu key={f.id} facet={f} values={selected[f.id] ?? []}
            onToggle={(v) => onToggle(f.id, v)} onClear={() => onClearFacet(f.id)}
            testid={testid ? `${testid}-facet-${f.id}` : undefined} />
        ))}
        {trailing ? <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>{trailing}</span> : null}
      </FilterBar>
      {anyFilter ? (
        <div style={{ padding: "0 24px 12px", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {chips.map((c) => (
            <span key={c.facet.id + "|" + c.value}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 6px 0 10px", borderRadius: 999, background: T.p4, border: `1px solid ${T.p3}`, color: T.p1, fontSize: 12, fontWeight: 600 }}>
              <span style={{ color: alpha(T.p1, 70), fontWeight: 500 }}>{c.facet.label}:</span>{c.label}
              <button type="button" aria-label={t("shell.composites.removeFilter", "Remove filter")} onClick={() => onToggle(c.facet.id, c.value)}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: 999, border: "none", background: "transparent", color: T.p1, cursor: "pointer" }}>
                <Icon.x size={10} />
              </button>
            </span>
          ))}
          <button type="button" onClick={onClearAll} data-testid={testid ? `${testid}-clear-all` : undefined}
            style={{ height: 26, padding: "0 10px", borderRadius: 999, border: "none", background: "transparent", color: T.p1, fontSize: 12, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
            {t("shell.composites.clearAll", "Clear all")}
          </button>
          {summary ? <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{summary}</span> : null}
        </div>
      ) : summary ? (
        <div style={{ padding: "0 24px 12px", fontSize: 12, color: T.soft, textAlign: "right" }}>{summary}</div>
      ) : null}
    </div>
  );
}

/**
 * RowActions — verbatim port of the standard row action cluster
 * (View · Edit · Duplicate · ⋯). Each control gets a role-suffixed, position-
 * independent testid from one `testid` prefix. Stops row-click propagation.
 */
export function RowActions({ onView, onEdit, onDuplicate, viewLabel = "View", editLabel = "Edit", duplicateLabel = "Duplicate", moreItems, moreHeader, moreWidth = 240, align = "right", size = 28, testid, audit }: {
  onView?: () => void; onEdit?: () => void; onDuplicate?: () => void;
  viewLabel?: string; editLabel?: string; duplicateLabel?: string;
  moreItems?: Array<MoreMenuItemSpec | "divider">; moreHeader?: string; moreWidth?: number; align?: "left" | "right"; size?: number; testid?: string;
  /** Admin-only: when set, appends a "View audit trail" item that opens the
   * shared change-history drawer for this object. Hidden for non-admins. */
  audit?: { type: AuditObjectType; id: string; name: string };
}) {
  const t = useT();
  const wrap = (fn?: () => void) => (e: MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); fn && fn(); };
  const tid = (suffix: string) => (testid ? `${testid}-${suffix}` : undefined);
  const [auditOpen, setAuditOpen] = useState(false);
  const showAudit = !!audit && isAdmin();
  const items: Array<MoreMenuItemSpec | "divider"> = [
    ...(moreItems ?? []),
    ...(showAudit ? ([...(moreItems && moreItems.length ? ["divider" as const] : []), { icon: <Icon.list size={14} />, label: t("shell.composites.viewAuditTrail", "View audit trail"), onClick: () => setAuditOpen(true) }]) : []),
  ];
  return (
    <>
    <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
      {onView ? <IconBtn size={size} label={viewLabel} testid={tid("view-btn")} onClick={wrap(onView)}><Icon.eye size={14} /></IconBtn> : null}
      {onEdit ? <IconBtn size={size} label={editLabel} testid={tid("edit-btn")} onClick={wrap(onEdit)}><Icon.edit size={14} /></IconBtn> : null}
      {onDuplicate ? <IconBtn size={size} label={duplicateLabel} testid={tid("duplicate-btn")} onClick={wrap(onDuplicate)}><Icon.copy size={14} /></IconBtn> : null}
      {items.length ? <MoreMenu size={size} header={moreHeader} width={moreWidth} items={items} testid={tid("more-btn")} /> : null}
    </div>
    {showAudit && auditOpen ? <AuditTrailDrawer target={audit!} onClose={() => setAuditOpen(false)} /> : null}
    </>
  );
}
